import React from 'react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import { SERIES } from '../lib/colors';
import { fmtCount, fmtSharePct, fmtMonthKey } from '../lib/format';

/**
 * Evidence for `brand_split`.
 *
 * `facts`: { items: [{ month, brand_clicks, nonbrand_clicks, brand_share }],
 *            summary: { brand_clicks, nonbrand_clicks, brand_share, nonbrand_share,
 *                       brand_impressions, nonbrand_impressions, brand_cores },
 *            period_start, period_end, comparison_available }
 *
 * `summary.brand_cores` is NEVER displayed (F15) — it is the token list used to classify
 * a query as branded, and it exists so a park whose split looks wrong can be diagnosed.
 * Showing it turns a business panel into a debugging surface.
 *
 * This panel has its OWN `comparison_available`, separate from `search_headline`'s —
 * F9 applies per panel, not page-wide, and in the live payload the two genuinely differ.
 *
 * Vocabulary (F19): brand = the search contained the park's name; discovery = it did not.
 */
const StackedShareChart = ({ facts, sentence }) => {
  const items = Array.isArray(facts?.items) ? facts.items : [];
  const summary = facts?.summary || {};
  const brandShare = fmtSharePct(summary.brand_share);
  const nonbrandShare = fmtSharePct(summary.nonbrand_share);

  // Colour maps the two sides consistently across the summary numbers and the chart areas:
  // the "searched by name" side (54%) is green, the discovery side (46%) purple. Both come
  // from the shared SERIES palette (identical in light and dark).
  const NAMED_COLOR = SERIES.discovery;   // green  #22C55E — "searched for you by name"
  const DISCOVERY_COLOR = SERIES.brand;   // purple #8B5CF6 — "found without your name"

  // One point per month at each side's actual share: brand is the payload's `brand_share`
  // (e.g. 0.54) and non-brand is its complement (0.46), equal to the backend's own
  // `summary.nonbrand_share`. Plotted as two OVERLAID areas (not stacked) so each reaches
  // its real level — 54% / 46% — rather than being forced to sum to 100%.
  const shareSeries = items
    .filter((it) => Number.isFinite(Number(it?.brand_share)))
    .map((it) => ({
      month: it.month,
      brand: Number(it.brand_share),
      nonbrand: 1 - Number(it.brand_share),
    }));

  return (
    <div className="space-y-4">
      {/* Each pair is gated on its own value: F18 says a missing field omits its
          element, and an ungated <dt> renders a label above an empty <dd> — a heading
          for a number that isn't there, which reads as a rendering fault. */}
      {(brandShare || nonbrandShare) && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {brandShare && (
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Searched for you by name</dt>
              <dd className="text-2xl font-bold tabular-nums">
                <span style={{ color: NAMED_COLOR }}>{brandShare}</span>
                {fmtCount(summary.brand_clicks) && (
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    {fmtCount(summary.brand_clicks)} visits
                  </span>
                )}
              </dd>
            </div>
          )}
          {nonbrandShare && (
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Found you without knowing your name</dt>
              <dd className="text-2xl font-bold tabular-nums">
                <span style={{ color: DISCOVERY_COLOR }}>{nonbrandShare}</span>
                {fmtCount(summary.nonbrand_clicks) && (
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    {fmtCount(summary.nonbrand_clicks)} visits
                  </span>
                )}
              </dd>
            </div>
          )}
        </dl>
      )}

      {shareSeries.length > 0 && (
        <div className="min-h-[240px]" role="img" aria-label={sentence || 'Share of visits by month, split by whether the search used the park name'}>
          {/* Two OVERLAID share areas (not stacked): each is drawn from 0 to its own share,
              so brand tops out near 54% and non-brand near 46% — their real levels — instead
              of a stack that always sums to 100% and pins the top edge flat. Semi-transparent
              fills so the overlap and the crossover stay visible. Axis is 0–100%; the tooltip
              shows each share as a percentage. */}
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={shareSeries} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonthKey} tick={{ fontSize: 11 }} stroke="#94A3B8" />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="#94A3B8"
                width={44}
                domain={[0, 1]}
                tickFormatter={(v) => `${Math.round(v * 100)}%`}
              />
              <ThemedChartTooltip labelFormatter={fmtMonthKey} formatter={(v) => fmtSharePct(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="brand"
                name="Searched by name"
                stroke={NAMED_COLOR}
                fill={NAMED_COLOR}
                fillOpacity={0.5}
              />
              <Area
                type="monotone"
                dataKey="nonbrand"
                name="Found without your name"
                stroke={DISCOVERY_COLOR}
                fill={DISCOVERY_COLOR}
                fillOpacity={0.4}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default StackedShareChart;
